<template>
  <div class="SearchResults">
    <div
      v-if="isQueryTooShort"
      class="no-results"
    >
      Type meer om te zoeken
    </div>
    <div
      v-else-if="showLoadingMessage"
      class="no-results"
    >
      Bezig met zoeken..
    </div>
    <div
      v-else-if="showNoResultsMessage"
      class="no-results"
    >
      Geen resultaten gevonden met deze zoektermen.
    </div>
    <div
      v-else
      class="search-results-wrapper"
    >
      <span
        ref="topSentinel"
        class="scroll-sentinel scroll-sentinel--top"
      />
      <UISearchResultSection
        v-if="hasSearchResultsEvents"
        title="Programma's"
        section-class="search-results-events"
      >
        <UISearchItem
          v-for="(entry, idx) in visibleEvents"
          :key="idx"
          :item="entry"
          type="event"
        />
      </UISearchResultSection>
      <UISearchResultSection
        v-if="hasSearchResultsPages"
        title="Pagina's"
        section-class="search-results-pages"
      >
        <UISearchItem
          v-for="(entry, idx) in visiblePages"
          :key="idx"
          :item="entry"
          :no-image="fullResults"
          type="page"
        />
      </UISearchResultSection>
      <UISearchResultSection
        v-if="hasSearchResultsFaqQuestions"
        title="Veelgestelde vragen"
        section-class="search-results-faq-questions"
      >
        <UIFaqItem
          v-for="entry in visibleFaqQuestions"
          :id="entry.id"
          :key="entry.id"
          :title="entry.question"
          :text="entry.answer"
          class="faq-result-item"
        />
      </UISearchResultSection>
      <span
        ref="bottomSentinel"
        class="scroll-sentinel scroll-sentinel--bottom"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import type { SearchFaqResult, SearchResult } from '~/types/search'

  const props = withDefaults(
    defineProps<{
      searchString: string
      fullResults?: boolean
      showPrograms?: boolean
      showPages?: boolean
      showFaqQuestions?: boolean
    }>(),
    {
      fullResults: false,
      showPrograms: true,
      showPages: true,
      showFaqQuestions: true,
    },
  )

  const MINIMUM_SEARCH_LENGTH = 3
  const MAX_PREVIEW_RESULTS = 3

  const debouncedSearch = ref('')
  const isLoading = ref(false)

  const searchResultsPages = ref<SearchResult[]>([])
  const searchResultsEvents = ref<SearchResult[]>([])
  const searchResultsFaqQuestions = ref<SearchFaqResult[]>([])

  const hasSearchResultsPages = computed(() => props.showPages && searchResultsPages.value.length > 0)
  const hasSearchResultsEvents = computed(() => props.showPrograms && searchResultsEvents.value.length > 0)
  const hasSearchResultsFaqQuestions = computed(
    () => props.showFaqQuestions && searchResultsFaqQuestions.value.length > 0,
  )
  const hasAnyResults = computed(
    () => hasSearchResultsPages.value || hasSearchResultsEvents.value || hasSearchResultsFaqQuestions.value,
  )

  // Priority order matches the template's if/else-if chain: too-short beats
  // loading, which beats the empty-results message, which beats the results view.
  const isQueryTooShort = computed(() => props.searchString.length < MINIMUM_SEARCH_LENGTH)
  const showLoadingMessage = computed(() => !isQueryTooShort.value && isLoading.value)
  const showNoResultsMessage = computed(() => !isQueryTooShort.value && !isLoading.value && !hasAnyResults.value)

  const visibleEvents = computed(() =>
    props.fullResults ? searchResultsEvents.value : searchResultsEvents.value.slice(0, MAX_PREVIEW_RESULTS),
  )
  const visiblePages = computed(() =>
    props.fullResults ? searchResultsPages.value : searchResultsPages.value.slice(0, MAX_PREVIEW_RESULTS),
  )
  const visibleFaqQuestions = computed(() =>
    props.fullResults ? searchResultsFaqQuestions.value : searchResultsFaqQuestions.value.slice(0, MAX_PREVIEW_RESULTS),
  )

  async function fetchSearchResults<T>(endpoint: string, resultsKey: string): Promise<T[]> {
    try {
      const response = await useBaseAsyncData<Record<string, T[]>>(`${endpoint}?keywords=${debouncedSearch.value}`)
      return response.data.value?.[resultsKey] ?? []
    } catch (error) {
      console.error(error)
      return []
    }
  }

  async function getResults() {
    if (debouncedSearch.value.length < MINIMUM_SEARCH_LENGTH) {
      return
    }

    searchResultsPages.value = props.showPages ? await fetchSearchResults<SearchResult>('/pages/search', 'pages') : []
    searchResultsEvents.value = props.showPrograms
      ? await fetchSearchResults<SearchResult>('/events/search', 'events')
      : []
    searchResultsFaqQuestions.value = props.showFaqQuestions
      ? await fetchSearchResults<SearchFaqResult>('/pages/search/faq', 'pages')
      : []

    isLoading.value = false
  }

  let stopWatch: (() => void) | null = null

  onMounted(() => {
    stopWatch = watch(
      () => props.searchString,
      debounce((newSearch: string) => {
        isLoading.value = true
        debouncedSearch.value = newSearch
        getResults()
      }, 300),
      { immediate: true },
    )
  })

  onUnmounted(() => {
    if (stopWatch) stopWatch()
  })

  // Fade the top/bottom edge of the scroll container while a sentinel at that edge is scrolled out of view.
  const topSentinel = ref<HTMLElement | null>(null)
  const bottomSentinel = ref<HTMLElement | null>(null)
  let observer: IntersectionObserver | null = null
  let fadeHost: HTMLElement | null = null

  function findScrollParent(el: HTMLElement | null): HTMLElement | null {
    let node = el?.parentElement
    while (node && node !== document.body) {
      const style = getComputedStyle(node)
      if (node.tagName === 'DIALOG' || /(auto|scroll)/.test(style.overflowY)) return node
      node = node.parentElement
    }
    return document.documentElement
  }

  watch(
    [topSentinel, bottomSentinel],
    ([top, bottom]) => {
      observer?.disconnect()
      fadeHost?.classList.remove('is-scrollable-top', 'is-scrollable-bottom')
      if (!top || !bottom) return
      const scrollParent = findScrollParent(top)
      fadeHost = scrollParent?.parentElement ?? null
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            const edge = entry.target === top ? 'is-scrollable-top' : 'is-scrollable-bottom'
            fadeHost?.classList.toggle(edge, !entry.isIntersecting)
          }
        },
        { root: scrollParent },
      )
      observer.observe(top)
      observer.observe(bottom)
    },
    { immediate: true },
  )

  onUnmounted(() => {
    observer?.disconnect()
    fadeHost?.classList.remove('is-scrollable-top', 'is-scrollable-bottom')
  })
</script>

<style lang="scss" scoped>
  .SearchResults {
    color: $color-text;
  }

  .no-results {
    padding: var(--padding-lg);
  }

  .search-results-wrapper {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: var(--margin-lg);
    padding: var(--padding-lg);
    background-color: $beige;
  }

  .scroll-sentinel {
    position: absolute;
    left: 0;
    width: 1px;
    height: 1px;
    pointer-events: none;

    &--top {
      top: 0;
    }

    &--bottom {
      bottom: 0;
    }
  }

  .search-results {
    .UISearchItem {
      margin-bottom: var(--margin-sm);

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
</style>
